/**
 * PhotoThumb — square hairline-bordered photo container.
 * Used in lists, galleries, and the in-flight upload preview.
 */
import * as React from 'react';
import { cn } from '../../lib/cn';

export interface PhotoThumbProps extends React.HTMLAttributes<HTMLElement> {
  src: string | null | undefined;
  alt: string;
  /** Square edge in pixels. Default 48. */
  size?: number;
  /** Optional caption rendered below the thumb in caption size. */
  caption?: string;
  /** When true, image renders at 60% opacity (optimistic upload). */
  optimistic?: boolean;
  /** Renders an error overlay with "Retry upload" link. */
  errorRetry?: () => void;
}

export function PhotoThumb({
  src,
  alt,
  size = 48,
  caption,
  optimistic,
  errorRetry,
  className,
  ...rest
}: PhotoThumbProps) {
  return (
    <figure
      className={cn('inline-block', className)}
      style={{ width: size }}
      {...rest}
    >
      <div
        className={cn(
          'relative border border-ink-200 bg-ink-100 overflow-hidden',
        )}
        style={{ width: size, height: size }}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            className={cn(
              'h-full w-full object-cover',
              optimistic && 'opacity-60',
            )}
          />
        ) : (
          <div className="h-full w-full skeleton" aria-hidden />
        )}
        {errorRetry ? (
          <div className="absolute inset-0 flex items-center justify-center bg-bg/80">
            <button
              type="button"
              onClick={errorRetry}
              className="text-[0.65rem] uppercase tracking-wider text-accent underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
            >
              Retry upload
            </button>
          </div>
        ) : null}
      </div>
      {caption ? (
        <figcaption className="mt-1.5 text-[0.75rem] text-ink-500 leading-snug">
          {caption}
        </figcaption>
      ) : null}
    </figure>
  );
}
